// WebsiteOverviewCard.tsx
import React from 'react';
import styled from 'styled-components';
import ViewIcon from '../../../../assets/icons/ViewIcon';
import EditIcon from '../../../../assets/icons/EditIcon';
import StyledText from '../../../../components/StyledComponents/StyledText';
import { useTheme } from '../../../../middleware/ThemeContext';
import { CorporateTheme, font, Theme } from '../../../../utils/themes';
import CalendarIcon from '../../../../assets/icons/CalendarIcon';
import { capitalizeFirstLetter, formatDateDefault } from '../../../../utils/helper';
import { SkeletonCell } from '../../../../components/SkeletonCell';
import { useNavigate } from 'react-router-dom';
import PageIcon from '../../../../assets/icons/PageIcon';
import NewPageIcon from '../../../../assets/icons/NewPageIcon';
import { getStatusLabel } from '../../../../utils/stausMapping';

// Main Card Container (Light Blue Background)
const CardContainer = styled.div<{ isResponsive?: boolean, theme: Theme }>`
  background-color: ${({ theme }) => theme.CorporateTheme.colors.lightBlueBg};
  border-radius: 0.75rem;
  padding: ${({ isResponsive }) => isResponsive ? '35px 10px' : '35px 30px'} ;
  position: relative;
  width:100%;
`;

// Header Section
const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 0.75rem;
`;

// Subtitle: "Website Information"
const SubTitle = styled.span`
  font-size: 0.75rem;
  color: #3b82f6;
  font-weight: 500;
`;

// Title: "Techtonic Overview"
const Title = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  margin: 0.5rem 0 0.25rem 0;
`;

// Domain Link
const DomainLink = styled.a<{ theme: Theme }>`
display:flex;
  align-items: center;
  gap:3px;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.CorporateTheme.colors.primary};
  text-decoration: none;
  margin-top:20px;
  &:hover {
    text-decoration: underline;
  }
`;

// Action Icons Wrapper
const ActionIcons = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

// Icon Button
export const IconButton = styled.div<{ isResponsive?: boolean, theme: Theme }>`
  background: white;
  border: none;
  border-radius: 50%;
  width: ${({ isResponsive }) => isResponsive ? '35px' : '44px'};
  height: ${({ isResponsive }) => isResponsive ? '35px' : '44px'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
  background-color: ${({ theme }) => theme.CorporateTheme.colors.background};

`;

// Not Published Badge
const Badge = styled.span<{ $published: boolean, theme: Theme, badgeBgColor?: string }>`
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0px 20px;
  border-radius: 18px;
  height:36px;
  display:flex;
  align-items: center;
  background-color: ${({ $published, theme, badgeBgColor }) => badgeBgColor ? badgeBgColor : theme.CorporateTheme.colors.green};
`;

// Stats Bar (White rounded box at bottom)
const StatsBar = styled.div<{ theme: Theme, isResponsive?: boolean }>`
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  border-radius: 0.5rem;
  padding: 0.75rem;
  display: flex;
  align-items: center;
  gap: ${({ isResponsive }) => isResponsive ? '10px' : '30px'} ;
  font-size: 0.875rem;
  color: #4b5563;
  margin-top: 30px;
`;

// Stat Item
const StatItem = styled.div<{ isResponsive?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${({ isResponsive }) => isResponsive ? '5px' : '10px'} ;
`;

// Calendar Icon (SVG inline for simplicity)


interface WebsiteOverviewCardProps {
  createdDate: string;
  lastModified: string;
  totalPublished: number;
  domain: string;
  customWebDomain?: string | null;
  title?: string;
  isPublished?: boolean;
  onEdit: () => void;
  onView: () => void;
  isResponsive?: boolean;
  companyLoading?: boolean;
  statusId?: string;
}

const WebsiteOverviewCard: React.FC<WebsiteOverviewCardProps> = ({
  title = "Techtonic Overview",
  domain,
  createdDate,
  lastModified,
  totalPublished,
  isPublished = false,
  onEdit,
  onView,
  isResponsive,
  companyLoading,
  statusId,
  customWebDomain
}) => {
  const { theme } = useTheme();
  const displayDomain = customWebDomain?.trim() || domain;

  const handleClick = () => {
    window.open(`https://${displayDomain}`, "_blank");
  };

  const formatUrl = (d: string) =>
    d.startsWith("http") ? d : `https://${d}`;

  return (
    <CardContainer isResponsive={isResponsive} theme={theme}>
      {/* Header Row */}
      <HeaderSection>
        <div>
          <StyledText fontSize='14px' fontWeight={500} textColor={theme.CorporateTheme.colors.primary}>Website Information</StyledText>
          {companyLoading ? <SkeletonCell height={isResponsive ? '20px' : '25px'} className='mt-4' /> : <StyledText fontSize={isResponsive ? '16px' : '20px'} fontWeight={600} textColor={theme.CorporateTheme.colors.headerColor} className='mt-4'>{title}</StyledText>}

          <DomainLink href={formatUrl(domain)} target="_blank" rel="noopener noreferrer" theme={theme}>
            <StyledText fontSize={isResponsive ? '13px' : '16px'} fontWeight={600} textColor={theme.CorporateTheme.colors.primary}>Domain: </StyledText>
            {companyLoading ? <SkeletonCell height={isResponsive ? '18px' : '20px'} width={isResponsive ? '100px' : '150px'} /> : <StyledText fontSize={isResponsive ? '13px' : '16px'} fontWeight={500} textColor={theme.CorporateTheme.colors.primary}>{domain}</StyledText>}
          </DomainLink>
        </div>

        <ActionIcons>
          <IconButton onClick={handleClick} aria-label="View" isResponsive={isResponsive} theme={theme}  >
            <ViewIcon size={isResponsive ? 18 : 22} color={theme.CorporateTheme.colors.headerColor} />
          </IconButton>
          <IconButton onClick={onEdit} aria-label="Edit" isResponsive={isResponsive} theme={theme} >
            <EditIcon size={isResponsive ? 18 : 22} color={theme.CorporateTheme.colors.headerColor} />
          </IconButton>
          <Badge $published={!isPublished} theme={theme} badgeBgColor={CorporateTheme.colors.green}>
            <StyledText fontSize={isResponsive ? '12px' : '14px'} fontWeight={500} textColor={theme.CorporateTheme.colors.white}>{capitalizeFirstLetter(getStatusLabel(Number(statusId)))}
            </StyledText>
          </Badge>
        </ActionIcons>
      </HeaderSection>

      {/* Stats Bar */}
      <StatsBar theme={theme} isResponsive={isResponsive}>
        <StatItem isResponsive={isResponsive}>
          <CalendarIcon color={theme.CorporateTheme.colors.heavyMetal} />
          <div className='d-flex align-items-center gap-1'>
            <StyledText fontSize={isResponsive ? '12px' : '14px'} fontWeight={500} textColor={theme.CorporateTheme.colors.headerColor}>Created Date:</StyledText>
            {companyLoading ? <SkeletonCell height={isResponsive ? '18px' : '20px'} width={'100px'} /> : <StyledText fontSize={isResponsive ? '12px' : '14px'} fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary} fontFamily={font.lato}>{formatDateDefault(new Date(createdDate))}</StyledText>}

          </div>
        </StatItem>
        <StatItem isResponsive={isResponsive}>
          <CalendarIcon color={theme.CorporateTheme.colors.heavyMetal} />
          <div className='d-flex align-items-center gap-1'>
            <StyledText fontSize={isResponsive ? '12px' : '14px'} fontWeight={500} textColor={theme.CorporateTheme.colors.headerColor}>Last Modified Date:</StyledText>
            {companyLoading ? <SkeletonCell height={isResponsive ? '18px' : '20px'} width={'100px'} /> : <StyledText fontSize={isResponsive ? '12px' : '14px'} fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary} fontFamily={font.lato}>{formatDateDefault(new Date(lastModified))}</StyledText>}

          </div>
          {/* <span>Last Modified Date: {lastModified}</span> */}
        </StatItem>
        <StatItem isResponsive={isResponsive}>
          <NewPageIcon color={theme.CorporateTheme.colors.heavyMetal} size={19} />
          <div className='d-flex align-items-center gap-1'>
            <StyledText fontSize={isResponsive ? '12px' : '14px'} fontWeight={500} textColor={theme.CorporateTheme.colors.headerColor}>Total Published Pages:</StyledText>
            {companyLoading ? <SkeletonCell height={isResponsive ? '18px' : '20px'} width={'10px'} /> : <StyledText fontSize={isResponsive ? '12px' : '14px'} fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary} fontFamily={font.lato}>{totalPublished}</StyledText>}
            <StyledText fontSize={isResponsive ? '12px' : '14px'} fontWeight={500} textColor={theme.CorporateTheme.colors.textSecondary}>{totalPublished === 1 ? 'Page' : 'Pages'}</StyledText>


          </div>
          {/* <span>Total Published Pages: {totalPublished} Pages</span> */}
        </StatItem>
      </StatsBar>
    </CardContainer>
  );
};

export default WebsiteOverviewCard;